if (!customElements.get('team-filters')) {
  class TeamFilters extends HTMLElement {
    constructor() {
      super();
      this.checkboxes = this.querySelectorAll('.team-filters__checkbox');
      this.clearButton = this.querySelector('.team-filters__clear');
      this.teamLists = document.querySelectorAll('team-list');

      this.filtersList = this.querySelector('.team-filters__list');

      this.filtersListScrollPath = Math.round(this.filtersList.scrollWidth / this.filtersList?.children.length)

      this.nextButton = this.querySelector('[data-next]');
      this.prevButton = this.querySelector('[data-prev]');

      // Store original cards and pagination for each team list
      this.originalCardsMap = new Map();
      this.originalPaginationMap = new Map();

      this.setupOriginalEntities();
      this.addEventListeners();
    }


    // Save original cards and pagination for each team list
    setupOriginalEntities() {
      this.teamLists.forEach(teamList => {
        const cards = Array.from(teamList.querySelectorAll('.team-card'));
        const pagination = teamList.querySelector('.team-list__pagination').innerHTML;
        this.originalCardsMap.set(teamList, cards);
        this.originalPaginationMap.set(teamList, pagination); // New
      });
    }

    // Add event listeners for checkboxes and clear button
    addEventListeners() {
      this.prevButton?.addEventListener('click', () => {
        this.filtersList.scrollLeft -= this.filtersListScrollPath;
        this.visibilityNavigation();

      });
      this.nextButton?.addEventListener('click', () => {
        this.filtersList.scrollLeft += this.filtersListScrollPath;
        this.visibilityNavigation();
      });

      this.checkboxes.forEach(checkbox =>
        checkbox.addEventListener('change', () => this.applyFilters())
      );

      this.clearButton.addEventListener('click', () => this.clearFilters());
    }


    visibilityNavigation() {
      setTimeout(() => {
        const isAtStart = Math.round(this.filtersList.getBoundingClientRect().left) === Math.round(this.filtersList.firstElementChild.getBoundingClientRect().left);
        const isAtEnd = Math.round(this.filtersList.getBoundingClientRect().right) === Math.round(this.filtersList.lastElementChild.getBoundingClientRect().right);

        if (isAtStart) {
          this.prevButton?.setAttribute('data-disabled', '');
        } else {
          this.prevButton?.removeAttribute('data-disabled');
        }

        if (isAtEnd) {
          this.nextButton?.setAttribute('data-disabled', '');
        } else {
          this.nextButton?.removeAttribute('data-disabled');
        }
      }, 300);
    }

    // Clear all filters and reset checkboxes
    clearFilters() {
      this.checkboxes.forEach(checkbox => checkbox.checked = false);
      this.applyFilters(true); // Reset to original when clearing filters
    }

    // Main function to apply or reset filters
    applyFilters(reset = false) {
      this.teamLists.forEach(teamList => {
        const items = teamList.querySelectorAll('.team-list__item');
        const cardsPerItem = parseInt(teamList.dataset.max, 10);
        const filteredCards = reset ? this.originalCardsMap.get(teamList) : this.getFilteredCards(teamList);

        // Reset to first page
        if (!reset) {
          const firstInput = teamList.querySelector('.team-list__pagination-input');
          if (firstInput) firstInput.checked = true;
        }

        this.clearItems(items);          // Clear current cards
        this.updatePagination(teamList, filteredCards.length, reset); // Update pagination
        this.distributeCards(items, filteredCards, cardsPerItem); // Distribute new cards

        // Trigger pagination change to update view
        if (!reset) {
          teamList.handlePaginationChange(0);
        }
      });
    }

    // Get filtered cards or return all original cards if no filter is applied
    getFilteredCards(teamList) {
      const activeFilters = Array.from(this.checkboxes).filter(checkbox => checkbox.checked);

      if (activeFilters.length === 0) {
        // Return original cards if no filter is applied
        return this.originalCardsMap.get(teamList);
      }

      // Filter cards based on active checkboxes
      const originalCards = this.originalCardsMap.get(teamList);
      return originalCards.filter(card =>
        activeFilters.some(filter => card.dataset.value === filter.value)
      );
    }

    // Clear all items before appending filtered or original cards
    clearItems(items) {
      items.forEach(item => item.innerHTML = '');
    }

    // Distribute the cards across items, respecting the max per item
    distributeCards(items, cards, cardsPerItem) {
      let cardIndex = 0;

      items.forEach(item => {
        let cardCount = 0;

        while (cardCount < cardsPerItem && cardIndex < cards.length) {
          item.appendChild(cards[cardIndex]);
          cardCount++;
          cardIndex++;
        }
      });
    }

    // Update pagination dynamically based on filtered cards or reset to original
    updatePagination(teamList, filteredCardCount, reset = false) {
      const paginationContainer = teamList.querySelector('.team-list__pagination');

      if (reset) {
        // Restore original pagination
        paginationContainer.innerHTML = this.originalPaginationMap.get(teamList);
      } else {
        // Create new pagination based on the filtered cards
        const totalPages = Math.ceil(filteredCardCount / parseInt(teamList.dataset.max, 10));
        const screen = teamList.dataset.screen;
        let paginationHTML = '';

        for (let i = 1; i <= totalPages; i++) {
          paginationHTML += `
            <input class="team-list__pagination-input" name="team-list-pagination-${screen}" id="${screen}-${i}" type="radio" ${i === 1 ? 'checked' : ''}>
            <label data-active for="${screen}-${i}" class="team-list__pagination-label">${i}</label>
          `;
        }
        paginationContainer.innerHTML = paginationHTML;
      }

      // Re-setup pagination functionality in the 'team-list' component
      teamList.setupVariables();
      teamList.setupPagination();
    }

  }

  customElements.define('team-filters', TeamFilters);
}


if (!customElements.get('team-list')) {
  class TeamList extends HTMLElement {
    constructor() {
      super();
      this.setupVariables();
      this.setupPagination();
    }

    // Setup variables
    setupVariables() {
      this.items = this.querySelectorAll('.team-list__item');
      this.paginationInputs = this.querySelectorAll('.team-list__pagination-input');

      this.paginationLabels = this.querySelectorAll('.team-list__pagination-label');
      this.totalItems = this.paginationInputs.length; // Total number of items
    }

    // Setup pagination event listeners
    setupPagination() {
      this.paginationInputs.forEach((input, index) => {
        input.addEventListener('change', () => this.handlePaginationChange(index));
      });
    }

    // Handle the pagination change when an input is selected
    handlePaginationChange(index) {
      this.clearActiveItems();
      this.setActiveItem(index);
      this.updatePagination(index);
      this.scrollToTop(); // Scroll to the top of the list
    }

    // Clear active state from all items
    clearActiveItems() {
      this.items.forEach(item => item.removeAttribute('data-active'));
    }

    // Set active state for the selected item
    setActiveItem(index) {
      this.items[index].setAttribute('data-active', '');
    }

    // Update pagination based on the selected index
    updatePagination(index) {
      this.resetPagination(); // Clear active states from all pagination labels
      this.showAllLabels(); // Show all labels always
    }

    // Reset active states from all pagination labels
    resetPagination() {
      this.paginationLabels.forEach(label => label.removeAttribute('data-active'));
    }

    // Show all labels when the total items are less than 5
    showAllLabels() {
      this.paginationLabels.forEach(label => label.setAttribute('data-active', ''));

    }



    //Scroll to top of the list
    scrollToTop() {
      if (this.dataset.screen === 'mobile') {
        window.scrollTo({
          top: 180,
          behavior: 'smooth'
        });
      }
    }
  }

  customElements.define('team-list', TeamList);
}
